import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Package, Boxes, Tag, Star, Users, Menu, Moon, Sun, LogOut, Store, FolderTree, AlertTriangle } from "lucide-react";
import { useAdminTheme } from "@/admin/AdminTheme";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";

const NAV = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard, exact: true },
  { label: "Orders", to: "/admin/orders", icon: ShoppingCart },
  { label: "Products", to: "/admin/products", icon: Package },
  { label: "Inventory", to: "/admin/inventory", icon: Boxes },
  { label: "Catalog", to: "/admin/catalog", icon: FolderTree },
  { label: "Low Stock", to: "/admin/stock-report", icon: AlertTriangle },
  { label: "Promotions", to: "/admin/promotions", icon: Tag },
  { label: "Reviews", to: "/admin/reviews", icon: Star },
  { label: "Customers", to: "/admin/customers", icon: Users },
  { label: "More", to: "/admin/more", icon: Menu },
];

const MOBILE_TABS = [NAV[0], NAV[1], NAV[2], NAV[3], NAV[9]];

export function AdminLayout() {
  const { c, dark, toggle } = useAdminTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const isActive = (n) => (n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to));

  return (
    <div style={{ minHeight: "100vh", background: c.bg, color: c.onSurface, fontFamily: "DM Sans" }}>
      {/* Top bar (desktop) */}
      {!isMobile && (
        <header style={{ position: "sticky", top: 0, zIndex: 40, background: c.surface, borderBottom: `1px solid ${c.border}` }}>
          <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "center", gap: 8, padding: "12px 24px" }}>
            <Link to="/admin" style={{ textDecoration: "none", display: "flex", flexDirection: "column", lineHeight: 1, marginRight: 20 }}>
              <span className="font-display" style={{ fontSize: 20, fontWeight: 700, color: c.primary }}>Garlic Admin</span>
              <span style={{ fontSize: 8.5, letterSpacing: 3, color: c.mutedText, marginTop: 2 }}>COCKPIT</span>
            </Link>
            <nav style={{ display: "flex", gap: 2, flexWrap: "nowrap", overflowX: "auto", minWidth: 0 }}>
              {NAV.map((n) => {
                const active = isActive(n);
                return (
                  <button key={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => navigate(n.to)}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 9, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                      background: active ? c.primarySoft : "transparent", color: active ? c.primarySoftText : c.onSurfaceSecondary, fontWeight: active ? 700 : 500, fontSize: 13 }}>
                    <n.icon size={15} /> {n.label}
                  </button>
                );
              })}
            </nav>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <IconBtn onClick={() => navigate("/")} title="View storefront"><Store size={17} /></IconBtn>
              <IconBtn onClick={toggle} title="Toggle theme">{dark ? <Sun size={17} /> : <Moon size={17} />}</IconBtn>
              <IconBtn onClick={() => { logout(); navigate("/login"); }} title="Sign out"><LogOut size={17} /></IconBtn>
            </div>
          </div>
        </header>
      )}

      {/* Mobile top bar */}
      {isMobile && (
        <header style={{ position: "sticky", top: 0, zIndex: 40, background: c.surface, borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" }}>
          <span className="font-display" style={{ fontSize: 19, fontWeight: 700, color: c.primary }}>Garlic Admin</span>
          <div style={{ display: "flex", gap: 6 }}>
            <IconBtn onClick={() => navigate("/")}><Store size={17} /></IconBtn>
            <IconBtn onClick={toggle}>{dark ? <Sun size={17} /> : <Moon size={17} />}</IconBtn>
          </div>
        </header>
      )}

      <main style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "18px 16px 90px" : "28px 24px 48px" }}>
        <Outlet />
      </main>

      {/* Mobile bottom tabs */}
      {isMobile && (
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40, display: "flex", background: c.surface, borderTop: `1px solid ${c.border}` }}>
          {MOBILE_TABS.map((n) => {
            const active = isActive(n);
            return (
              <button key={n.to} data-testid={`admin-tab-${n.label.toLowerCase()}`} onClick={() => navigate(n.to)}
                style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: "10px 0 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, color: active ? c.primary : c.mutedText }}>
                <n.icon size={20} strokeWidth={active ? 2.2 : 1.7} />
                <span style={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>{n.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );

  function IconBtn({ children, ...rest }) {
    return <button style={{ background: c.surfaceAlt, border: `1px solid ${c.border}`, borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: c.onSurfaceSecondary }} {...rest}>{children}</button>;
  }
}
